import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { waitForPromise } from '@ember/test-waiters';
import { isEmpty } from '@ember/utils';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pluralize } from 'ember-inflector';
import { resource, use } from 'ember-resources';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';
import AdminEventData from 'garaje/components/event-log/admin-event-data';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import SensitiveFieldShield from 'garaje/components/sensitive-field-shield';
import TableDrawer from 'garaje/components/table-drawer';
import UiCard from 'garaje/components/ui/card';
import UiButton from 'garaje/components/ui-button';
import dateFormat from 'garaje/helpers/date-format';
import type EventLogModel from 'garaje/models/event-log';
import type MetricsService from 'garaje/services/metrics';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import { UTC_TIMEZONE_ALIASES } from 'garaje/utils/event-log/utc-timezone-aliases';
import { cached } from 'tracked-toolbox';

interface EventDataComponentSignature {
  Args: {
    eventType: string;
    eventData: string;
    formatValue: (value: unknown) => string;
  };
}

interface EventLogDrawerSignature {
  Args: {
    event: EventLogModel;
    isOpen: boolean;
    onClose: () => void;
    timezone?: string;
  };
  Blocks: {
    default?: [EventLogModel];
  };
}

interface DrawerRowSignature {
  Args: { label: string; testId?: string };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerRow: TOC<DrawerRowSignature> = <template>
  <div
    class="py-3 first:pt-0 last:pb-0 border-b border-carbon-10 last:border-b-0"
    data-test-drawer-row={{@testId}}
    ...attributes
  >
    <div class="text-sm text-carbon-40 mb-1">{{@label}}</div>
    <div class="text-sm text-carbon-70 break-words">{{yield}}</div>
  </div>
</template>;

type ChangeValue = {
  from: string | number | boolean | null | Record<string, unknown>;
  to: string | number | boolean | null | Record<string, unknown>;
};

type ChangesRecord = Record<string, ChangeValue>;

const HIDDEN_SENTINEL = '[HIDDEN]';

interface SensitiveRevealButtonSignature {
  Args: {
    revealed: boolean;
    onToggle: () => void;
  };
  Element: HTMLButtonElement;
}

const SensitiveRevealButton: TOC<SensitiveRevealButtonSignature> = <template>
  <button
    type="button"
    class="inline-flex items-center align-middle ml-1 text-carbon-50"
    aria-label={{if @revealed "Hide value" "Reveal value"}}
    aria-pressed={{if @revealed "true" "false"}}
    data-test-sensitive-reveal-toggle
    {{on "click" @onToggle}}
    ...attributes
  >
    {{svgJar
      (if @revealed "sensitive-eye-slashed" "open-eye-grayLight")
      width="16"
      height="16"
      class="[&_path]:fill-current [&_circle]:fill-current"
    }}
  </button>
</template>;

// date detection patterns
const TIMESTAMP_PATTERN = /^\d{13}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?$/;

// key formatting patterns
const UNDERSCORE_PATTERN = /_/g;
const CAMEL_CASE_PATTERN = /([a-z])([A-Z])/g;

// field exclusions mapping by event type pattern
const FIELD_EXCLUSIONS: Record<string, string[]> = {
  // default exclusions for all event types
  '*': ['changes', 'session_id', 'sensitive_fields'],

  'employee.*': ['employee_id', 'cost_center', 'external_id', 'remote_id', 'scim_user_id', 'user_id', 'location_type'],
  'integration.*': ['plugin_id', 'company_id'],
};

// fields that should only be displayed if they have non-empty values
const CONDITIONAL_DISPLAY_FIELDS: Record<string, string[]> = {
  'employee.*': ['phone_number', 'department', 'division', 'title', 'organization', 'manually_added', 'do_not_sync'],
};

function isChangesRecord(changes: unknown): changes is ChangesRecord {
  return changes !== null && typeof changes === 'object' && !Array.isArray(changes);
}

function isFromToObject(obj: unknown): obj is ChangeValue {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj) && 'from' in obj && 'to' in obj;
}

// Reactively fetches the actor's email for a User-typed event. Constructed
// fresh by the `actorEmailLoader` resource whenever `actorId` / `isUserActor`
// change, which is what guarantees no stale-email flash between events: a new
// instance starts with `email = null` and `isLoading = true`.
//
// `cancel()` is called by the resource's `on.cleanup` when args change or the
// component is torn down, so an in-flight fetch's late-arriving result never
// writes tracked state on an orphaned loader.
class ActorEmailLoader {
  @tracked email: string | null = null;
  @tracked isLoading = false;
  #cancelled = false;

  constructor(store: Store, actorId: string | undefined) {
    if (!actorId) return;
    this.isLoading = true;
    void this.#load(store, actorId);
  }

  cancel(): void {
    this.#cancelled = true;
  }

  async #load(store: Store, actorId: string): Promise<void> {
    try {
      // waitForPromise registers the promise with `settled()` in dev/test
      // builds so tests block on this fetch the same way they did for the
      // prior restartableTask. It compiles to a no-op in production.
      const user = await waitForPromise(store.findRecord('user', actorId));
      if (this.#cancelled) return;
      this.email = user?.email ?? null;
    } catch {
      if (this.#cancelled) return;
      this.email = null;
    } finally {
      if (!this.#cancelled) this.isLoading = false;
    }
  }
}

export default class EventLogDrawer extends Component<EventLogDrawerSignature> {
  @service declare metrics: MetricsService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare store: Store;

  @tracked collapsedFields = new Set<string>();

  constructor(owner: unknown, args: EventLogDrawerSignature['Args']) {
    super(owner, args);
    if (args.isOpen) {
      this.#trackDrawerOpen();
    }
  }

  get isSystemActor(): boolean {
    return this.args.event.actorType?.toLowerCase() === 'system';
  }

  get isUserActor(): boolean {
    return this.args.event.actorType?.toLowerCase() === 'user';
  }

  // The `on` parameter is annotated inline because ember-eslint-parser does
  // not currently flow ember-resources' inferred `ResourceAPI` type through
  // the `resource<T>` overload — without the annotation typescript-eslint
  // reports `on.cleanup` as an `any`-typed call.
  @use actorEmailLoader = resource<ActorEmailLoader>(
    ({ on }: { on: { cleanup: (destructor: () => void) => void } }) => {
      const loader = new ActorEmailLoader(this.store, this.isUserActor ? this.args.event.actorId : undefined);
      on.cleanup(() => loader.cancel());
      return loader;
    },
  );

  @cached
  get eventData(): Record<string, unknown> {
    try {
      return <Record<string, unknown>>JSON.parse(this.args.event.event);
    } catch {
      return {};
    }
  }

  get changes(): ChangesRecord | null {
    const eventData = this.eventData;
    const changes = eventData['changes'];

    if (!isChangesRecord(changes)) {
      return null;
    }

    return this.#flattenChanges(changes);
  }

  get hasChanges(): boolean {
    return this.changes !== null && Object.keys(this.changes).length > 0;
  }

  get eventDataEntries(): Array<{
    key: string;
    value: unknown;
    isNested?: boolean;
    level?: number;
    id?: string;
    parentId?: string;
  }> {
    return this.#flattenEventData(this.eventData, 0, '');
  }

  get lowercaseActorType(): string {
    const actorType = this.args.event.actorType?.toLowerCase().replace(/_/g, ' ') || '';
    if (actorType === 'system') return '';
    return actorType;
  }

  get lowercaseTargetType(): string {
    return this.args.event.targetType?.toLowerCase().replace(/_/g, ' ') || '';
  }

  get displayImpactedItems(): Array<{ name: string; type: string; id: string }> {
    const explicit = this.args.event.impactedItems;
    if (Array.isArray(explicit) && explicit.length > 0) {
      return explicit;
    }
    const { targetName, targetType, targetId } = this.args.event;
    if (targetName) {
      return [{ name: targetName, type: targetType ?? '', id: targetId ?? '' }];
    }
    return [];
  }

  get hasMultipleImpactedItems(): boolean {
    return this.displayImpactedItems.length > 1;
  }

  get isMoveRequestEvent(): boolean {
    return (this.args.event.eventType ?? '').startsWith('move_request.');
  }

  // Allowlist: only move_request.* surfaces session_id. auth.* also publishes
  // session_id (Rack session public_id) and must not reach this row.
  get sessionId(): string | null {
    if (!this.isMoveRequestEvent) return null;
    // Producer contract is snake_case; camelCase drift should fail loudly (missing row).
    const value = this.eventData['session_id'];
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed || null;
  }

  // The move-request details pane (per Figma) doesn't surface an IP address
  // row. The base event schema in `event-logs-validation` requires
  // `ip_address` to be a non-nullable string, so the producer can't omit it
  // — the gate has to live here. Scoped to `move_request.*` because that's
  // the only surface with the redesign; other event types keep their IP row.
  get suppressIpAddress(): boolean {
    return this.isMoveRequestEvent;
  }

  lowercaseImpactedType = (type: string | null | undefined): string => {
    return type?.toLowerCase().replace(/_/g, ' ') || '';
  };

  get visitorTargetLink(): { route: string; model: string; label: string } | null {
    const { targetType, targetId } = this.args.event;
    if (!targetId) return null;

    const normalizedType = targetType?.toLowerCase();
    if (normalizedType === 'invite') {
      return { route: 'visitors.invites.show', model: targetId, label: 'Invite' };
    }
    if (normalizedType === 'entry') {
      return { route: 'visitors.entry', model: targetId, label: 'Entry' };
    }

    return null;
  }

  get eventCategoryComponent(): typeof Component<EventDataComponentSignature> | null {
    if (this.args.event.eventType.startsWith('admin.')) {
      return AdminEventData as typeof Component<EventDataComponentSignature>;
    }

    // teams can add their category components here
    // e.g., if (this.isVisitorEvent) return VisitorEventData;
    return null;
  }

  @action
  close(): void {
    this.args.onClose();
  }

  @action
  toggleFieldCollapse(fieldId: string): void {
    if (this.collapsedFields.has(fieldId)) {
      this.collapsedFields.delete(fieldId);
    } else {
      this.collapsedFields.add(fieldId);
    }
    this.collapsedFields = new Set(this.collapsedFields);
  }

  isFieldCollapsed = (fieldId: string | undefined): boolean => {
    if (!fieldId) return false;
    return this.collapsedFields.has(fieldId);
  };

  formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.formatValue(item)).join(', ');
    }

    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const stringValue = String(value);

    // handle timestamps (13-digit numbers)
    if (TIMESTAMP_PATTERN.test(stringValue)) {
      const date = new Date(parseInt(stringValue, 10));
      if (!isNaN(date.getTime())) {
        return this.formatDate(date);
      }
    }

    // handle ISO date strings
    if (ISO_DATE_PATTERN.test(stringValue)) {
      const isoDate = new Date(stringValue);
      const eventTime = this.args.event.dateTime?.getTime();

      // if the ISO date matches the event timestamp (within 1 second), use the event timestamp for millisecond precision
      if (eventTime && Math.abs(isoDate.getTime() - eventTime) < 1000) {
        return this.formatDate(this.args.event.dateTime!);
      }

      if (!isNaN(isoDate.getTime())) {
        return this.formatDate(isoDate);
      }
    }

    return stringValue;
  };

  formatDate = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      hour12: true,
      timeZoneName: 'short',
      ...(this.args.timezone ? { timeZone: this.args.timezone } : {}),
    });
  };

  formatTimeWithMilliseconds = (date: Date): string => {
    return this.#formatTime(date, true);
  };

  // Browser-locale-aware date (e.g. "Aug 24, 2025" / "24 Aug 2025" / "2025年8月24日")
  formatLocalizedDate = (date: Date, timezone?: string): string => {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    });
  };

  // hour12 is intentionally omitted — the browser locale's default hourCycle wins (12h for en-US, 24h for de-DE, etc.)
  formatLocalizedTimeWithMilliseconds = (date: Date, timezone?: string): string => {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      timeZoneName: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    });
  };

  get hideUtcEquivalent(): boolean {
    return UTC_TIMEZONE_ALIASES.has(this.args.timezone ?? '');
  }

  formatKey = (key: string): string => {
    // handle special field name mappings
    if (key === 'default_location_id') {
      return 'Primary location';
    }
    if (key === 'entry_retention_period') {
      return 'Visitor data retention period';
    }

    // handle nested keys with dots (e.g. 'field.nested' -> 'Field > Nested')
    if (key.includes('.')) {
      return key
        .split('.')
        .map((part) => {
          const formatted = part.replace(UNDERSCORE_PATTERN, ' ').replace(CAMEL_CASE_PATTERN, '$1 $2').toLowerCase();
          return capitalize(formatted);
        })
        .join(' > ');
    }

    const formatted = key.replace(UNDERSCORE_PATTERN, ' ').replace(CAMEL_CASE_PATTERN, '$1 $2').toLowerCase();
    return capitalize(formatted);
  };

  isMaskedValue = (value: unknown): boolean => {
    return value === HIDDEN_SENTINEL;
  };

  isMaskedChange = (value: ChangeValue): boolean => {
    return this.isMaskedValue(value.from) || this.isMaskedValue(value.to);
  };

  get sensitiveFields(): string[] {
    const fields = this.eventData['sensitive_fields'];
    return Array.isArray(fields) ? (fields as string[]) : [];
  }

  isRevealableChange = (key: string, value: ChangeValue): boolean => {
    return this.sensitiveFields.includes(key) && !this.isMaskedChange(value);
  };

  isRevealableDetail = (key: string | undefined, value: unknown): boolean => {
    return typeof key === 'string' && this.sensitiveFields.includes(key) && !this.isMaskedValue(value);
  };

  isCollapsedChangeSide = (key: string, value: ChangeValue, side: ChangeValue['from']): boolean => {
    return this.isRevealableChange(key, value) && side !== null && !this.isSensitiveRevealed('changes', key);
  };

  @tracked revealedSensitiveFields = new Set<string>();

  isSensitiveRevealed = (section: string, key: string | undefined): boolean => {
    return this.revealedSensitiveFields.has(`${this.args.event.id}:${section}:${key}`);
  };

  toggleSensitiveReveal = (section: string, key: string | undefined): void => {
    const revealKey = `${this.args.event.id}:${section}:${key}`;
    const revealed = new Set(this.revealedSensitiveFields);
    if (revealed.has(revealKey)) {
      revealed.delete(revealKey);
    } else {
      revealed.add(revealKey);
    }
    this.revealedSensitiveFields = revealed;
  };

  displayChangeValue = (value: string | number | boolean | null | Record<string, unknown>, key?: string): string => {
    if (value === null || value === undefined) {
      return '(blank)';
    }

    // handle object values by converting to formatted JSON
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }

    // special handling for location IDs to show location names
    if (key === 'default_location_id') {
      return this.#getLocationName(String(value));
    }

    // append "day"/"days" suffix for retention period values
    if (key === 'entry_retention_period' && typeof value === 'number') {
      return `${value} ${value === 1 ? 'day' : pluralize('day')}`;
    }

    return this.formatValue(value);
  };

  getIndentationStyle = (level?: number): SafeString => {
    if (!level || level === 0) return htmlSafe('');
    return htmlSafe(`margin-left: ${level * 16}px;`);
  };

  includeBorder = (level: number | undefined, index: number): boolean => {
    return index > 0 && level === 0;
  };

  #trackDrawerOpen(): void {
    this.metrics.trackEvent('Event Log Drawer Opened', {
      event_type: this.args.event.eventType,
      company_id: this.args.event.companyId,
      location_id: this.args.event.locationId,
      actor_type: this.args.event.actorType,
      target_type: this.args.event.targetType,
    });
  }

  #getLocationName = (locationId: string): string => {
    if (locationId) {
      const location = this.skinnyLocations.content?.find((loc) => {
        return loc.id === locationId;
      });
      return location ? location.name : 'Unknown location';
    }
    return '-';
  };

  #flattenEventData(
    obj: Record<string, unknown>,
    level: number,
    parentId: string,
  ): Array<{ key: string; value: unknown; isNested?: boolean; level?: number; id?: string; parentId?: string }> {
    const entries: Array<{
      key: string;
      value: unknown;
      isNested?: boolean;
      level?: number;
      id?: string;
      parentId?: string;
    }> = [];
    const excludedFields = this.#getExcludedFields(this.args.event.eventType);
    const conditionalFields = this.#getConditionalDisplayFields(this.args.event.eventType);

    Object.entries(obj).forEach(([originalKey, value]) => {
      // skip fields that are in the exclusion list
      if (excludedFields.includes(originalKey)) {
        return;
      }

      // skip conditional fields that should be hidden
      if (conditionalFields.includes(originalKey) && this.#shouldHideConditionalField(value)) {
        return;
      }

      const formattedKey = this.formatKey(originalKey);
      const fieldId = parentId ? `${parentId}.${originalKey}` : originalKey;

      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          // handle arrays
          if (value.length === 0) {
            entries.push({ key: formattedKey, value: null, level, id: fieldId });
          } else {
            // check if array contains objects
            const hasObjects = value.some((item) => typeof item === 'object' && item !== null && !Array.isArray(item));

            if (hasObjects) {
              // array with objects - add array header and flatten each object
              entries.push({ key: formattedKey, value: '', isNested: true, level, id: fieldId });

              if (!this.isFieldCollapsed(fieldId)) {
                value.forEach((item, index) => {
                  const itemId = `${fieldId}[${index}]`;
                  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                    // object in array - add index header and flatten the object
                    entries.push({
                      key: `Item ${index + 1}`,
                      value: '',
                      isNested: true,
                      level: level + 1,
                      id: itemId,
                      parentId: fieldId,
                    });
                    if (!this.isFieldCollapsed(itemId)) {
                      const nestedEntries = this.#flattenEventData(item as Record<string, unknown>, level + 2, itemId);
                      entries.push(...nestedEntries);
                    }
                  } else {
                    // non-object in array - add as regular entry
                    entries.push({
                      key: `Item ${index + 1}`,
                      value: item,
                      level: level + 1,
                      id: itemId,
                      parentId: fieldId,
                    });
                  }
                });
              }
            } else {
              // array with primitives only
              entries.push({ key: formattedKey, value: value, level, id: fieldId });
            }
          }
        } else {
          // handle objects
          const objectKeys = Object.keys(value as Record<string, unknown>);
          if (objectKeys.length === 0) {
            // empty object - treat as a regular value
            entries.push({ key: formattedKey, value: null, level, id: fieldId });
          } else {
            // add the parent object key
            entries.push({ key: formattedKey, value: '', isNested: true, level, id: fieldId });

            // add nested entries with increased level only if not collapsed
            if (!this.isFieldCollapsed(fieldId)) {
              const nestedEntries = this.#flattenEventData(value as Record<string, unknown>, level + 1, fieldId);
              entries.push(...nestedEntries);
            }
          }
        }
      } else {
        // handle location ID resolution for event data
        const processedValue = originalKey === 'default_location_id' ? this.#getLocationName(String(value)) : value;
        entries.push({ key: formattedKey, value: processedValue, level, id: fieldId, parentId });
      }
    });

    return entries;
  }

  #formatTime = (date: Date, includeMilliseconds: boolean = false): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
      ...(this.args.timezone ? { timeZone: this.args.timezone } : {}),
    };

    if (includeMilliseconds) {
      options.fractionalSecondDigits = 3;
    }

    return date.toLocaleString('en-US', options);
  };

  /**
   * Gets all fields that should be excluded from display for a given event type.
   * This is needed to hide sensitive or unnecessary data like internal IDs
   * from the event log drawer. Combines default exclusions with specific event-type ones.
   */
  #getExcludedFields(eventType: string): string[] {
    const excludedFields = new Set<string>();

    // add default exclusions
    const defaultExclusions = FIELD_EXCLUSIONS['*'] || [];
    defaultExclusions.forEach((field) => excludedFields.add(field));

    // check for pattern matches (e.g., 'employee.*')
    Object.keys(FIELD_EXCLUSIONS).forEach((pattern) => {
      if (pattern !== '*' && this.#matchesPattern(eventType, pattern)) {
        const fields = FIELD_EXCLUSIONS[pattern] || [];
        fields.forEach((field) => excludedFields.add(field));
      }
    });

    // check for exact match
    const exactMatch = FIELD_EXCLUSIONS[eventType];
    if (exactMatch) {
      exactMatch.forEach((field) => excludedFields.add(field));
    }

    // for location.updated events where only entry_retention_period changed,
    // hide all location fields except address, active in Details
    // (entry_retention_period appears in Changes section, name appears in event header)
    if (eventType === 'location.updated' && this.#isRetentionPeriodOnlyChange()) {
      const keepFields = new Set(['address', 'active']);
      Object.keys(this.eventData).forEach((field) => {
        if (!keepFields.has(field)) excludedFields.add(field);
      });
    }

    return Array.from(excludedFields);
  }

  #isRetentionPeriodOnlyChange(): boolean {
    const changes = this.eventData['changes'];
    if (!changes || typeof changes !== 'object' || Array.isArray(changes)) return false;
    const keys = Object.keys(changes);
    return keys.length === 1 && keys[0] === 'entry_retention_period';
  }

  /**
   * Gets fields that should only be displayed when they have non-empty values.
   * This allows certain fields to be conditionally shown based on their content.
   */
  #getConditionalDisplayFields(eventType: string): string[] {
    const conditionalFields = new Set<string>();

    // check for pattern matches (e.g., 'employee.*')
    Object.keys(CONDITIONAL_DISPLAY_FIELDS).forEach((pattern) => {
      if (this.#matchesPattern(eventType, pattern)) {
        const fields = CONDITIONAL_DISPLAY_FIELDS[pattern] || [];
        fields.forEach((field) => conditionalFields.add(field));
      }
    });

    // check for exact match
    const exactMatch = CONDITIONAL_DISPLAY_FIELDS[eventType];
    if (exactMatch) {
      exactMatch.forEach((field) => conditionalFields.add(field));
    }

    return Array.from(conditionalFields);
  }

  /**
   * Determines if a conditional field should be hidden based on its value.
   * Note: for boolean fields, we show both true and false values.
   */
  #shouldHideConditionalField(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return false;
    }
    return isEmpty(value);
  }

  /**
   * Checks if an event type matches a pattern from the exclusion mapping.
   * This enables wildcard matching like 'employee.*' to match 'employee.created'.
   * Used to apply exclusion rules to entire event type categories without specifying each event individually.
   */
  #matchesPattern(eventType: string, pattern: string): boolean {
    if (pattern.endsWith('*')) {
      const prefix = pattern.slice(0, -1);
      return eventType.startsWith(prefix);
    }
    return eventType === pattern;
  }

  /**
   * Recursively flattens nested changes objects until we find from/to pairs.
   * Converts { field: { nested: { from: 'a', to: 'b' } } }
   * to { 'field.nested': { from: 'a', to: 'b' } }
   *
   * If an object doesn't contain any from/to pairs at any level, it will be ignored.
   */
  #flattenChanges(obj: Record<string, unknown>, prefix = ''): ChangesRecord {
    const flattened: ChangesRecord = {};

    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (isFromToObject(value)) {
        // found a from/to pair, add it to the flattened result
        flattened[fullKey] = value;
      } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // still a nested object, continue flattening recursively
        const nestedFlattened = this.#flattenChanges(value as Record<string, unknown>, fullKey);
        Object.assign(flattened, nestedFlattened);
      }
      // if it's not a from/to object and not a nested object, ignore it
      // this prevents primitive values or arrays from being treated as changes
    });

    return flattened;
  }

  <template>
    {{#if @isOpen}}
      <TableDrawer data-test-event-log-drawer>
        {{#if (has-block)}}
          {{! Teams can provide custom drawer content }}
          {{yield @event}}
        {{else}}
          {{! Default drawer content }}
          <div class="w-full p-8">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm text-carbon-40 font-medium mb-6" data-test-event-datetime>
                  {{#if @event.dateTime}}
                    {{dateFormat @event.dateTime "MMM d, yyyy" timezone=@timezone}}
                    &bull;
                    {{this.formatTimeWithMilliseconds @event.dateTime}}
                  {{else}}
                    -
                  {{/if}}
                </p>
                <h3 class="mb-1">{{if @event.eventTypeName @event.eventTypeName "-"}}</h3>
                <p class="text-sm text-carbon-70 mb-0" data-test-event-description>
                  {{if @event.eventDescription @event.eventDescription "-"}}
                </p>
              </div>
              <UiButton
                class="btn btn--small btn--outlineFill !bg-white"
                data-test-close-button
                {{on "click" this.close}}
              >
                {{svgJar "cross-black" height="16" width="16" class="-mb-1"}}
              </UiButton>
            </div>
          </div>

          <div class="flex w-full px-8 gap-4 items-stretch">
            <div class="{{if this.displayImpactedItems.length 'w-1/2' 'w-full'}} flex flex-col">
              <p class="text-sm text-carbon-40 font-medium !mb-0">Actor</p>
              <UiCard class="border-carbon-10 min-h-[76px] flex-1" data-private>
                <div class="flex items-center gap-2 h-full">
                  {{#if this.isSystemActor}}
                    <div class="mx-1 w-10 h-10 bg-carbon-10 rounded-full flex items-center justify-center">
                      <span class="flex" style="padding-left: 0.05rem;">
                        {{svgJar "logo-small-red" width="18" height="18"}}
                      </span>
                    </div>
                  {{else}}
                    <MonogramOrPhoto
                      class="!p-0"
                      @name={{if @event.actorName @event.actorName "-"}}
                      @medium={{true}}
                      @borderBottom={{false}}
                    />
                  {{/if}}
                  <div>
                    <p class="text-sm text-carbon-70 !mb-0" data-test-event-actor>
                      {{if @event.actorName @event.actorName "-"}}
                    </p>
                    {{#if this.lowercaseActorType}}
                      <p class="text-sm text-carbon-40 !mb-0" data-test-event-actor-type>
                        {{this.lowercaseActorType}}
                      </p>
                    {{/if}}
                  </div>
                </div>
              </UiCard>
            </div>

            {{#if this.displayImpactedItems.length}}
              <div class="w-1/2 min-w-0 flex flex-col">
                <p class="text-sm text-carbon-40 font-medium !mb-0" data-test-impacted-items-label>
                  {{if this.hasMultipleImpactedItems "Impacted items" "Impacted item"}}
                </p>
                {{#if this.hasMultipleImpactedItems}}
                  <div class="flex flex-1 gap-4 overflow-x-auto items-stretch" data-test-impacted-items-scroll>
                    {{#each this.displayImpactedItems as |item|}}
                      <UiCard
                        class="border-carbon-10 min-h-[76px] min-w-[220px] shrink-0 h-full"
                        data-test-impacted-item={{item.id}}
                        data-private
                      >
                        <div class="flex items-center gap-2 h-full">
                          <MonogramOrPhoto class="!p-0" @name={{item.name}} @medium={{true}} @borderBottom={{false}} />
                          <div>
                            <p class="text-sm text-carbon-70 !mb-0" data-test-event-target>
                              {{item.name}}
                            </p>
                            {{#if item.type}}
                              <p class="text-sm text-carbon-40 !mb-0" data-test-event-target-type>
                                {{this.lowercaseImpactedType item.type}}
                              </p>
                            {{/if}}
                          </div>
                        </div>
                      </UiCard>
                    {{/each}}
                  </div>
                {{else}}
                  {{#each this.displayImpactedItems as |item|}}
                    <UiCard class="border-carbon-10 min-h-[76px] flex-1" data-private>
                      <div class="flex items-center gap-2 h-full">
                        <MonogramOrPhoto class="!p-0" @name={{item.name}} @medium={{true}} @borderBottom={{false}} />
                        <div>
                          <p class="text-sm text-carbon-70 !mb-0" data-test-event-target>
                            {{item.name}}
                          </p>
                          {{#if item.type}}
                            <p class="text-sm text-carbon-40 !mb-0" data-test-event-target-type>
                              {{this.lowercaseImpactedType item.type}}
                            </p>
                          {{/if}}
                        </div>
                      </div>
                    </UiCard>
                  {{/each}}
                {{/if}}
              </div>
            {{/if}}
          </div>

          <div class="w-full px-8 py-8 flex flex-col gap-8">
            {{#if this.hasChanges}}
              <div>
                <p class="text-sm text-carbon-40 font-medium !mb-0">Changes</p>
                <UiCard class="border-carbon-10" data-test-event-changes>
                  <div class="divide-y divide-carbon-10">
                    {{#each-in this.changes as |key value|}}
                      <div class="py-3 first:pt-0 last:pb-0">
                        <div class="text-sm text-carbon-40 mb-1">
                          {{this.formatKey key}}
                          {{#if (this.isMaskedChange value)}}
                            <SensitiveFieldShield />
                          {{else if (this.isRevealableChange key value)}}
                            <SensitiveRevealButton
                              @revealed={{this.isSensitiveRevealed "changes" key}}
                              @onToggle={{fn this.toggleSensitiveReveal "changes" key}}
                            />
                          {{/if}}
                        </div>
                        <div class="text-sm text-carbon-70 break-words">
                          {{#if (this.isMaskedValue value.from)}}
                            <span class="text-carbon-40 italic" data-test-masked-change-from>Hidden</span>
                          {{else if (this.isCollapsedChangeSide key value value.from)}}
                            <span class="text-carbon-40 italic" data-test-collapsed-change-from>Hidden</span>
                          {{else}}
                            <span class="text-carbon-40">
                              {{this.displayChangeValue value.from key}}
                            </span>
                          {{/if}}
                          <span class="text-gem-50 mx-1">&rarr;</span>
                          {{#if (this.isMaskedValue value.to)}}
                            <span class="italic" data-test-masked-change-to>Hidden</span>
                          {{else if (this.isCollapsedChangeSide key value value.to)}}
                            <span class="italic" data-test-collapsed-change-to>Hidden</span>
                          {{else}}
                            <span>
                              {{this.displayChangeValue value.to key}}
                            </span>
                          {{/if}}
                        </div>
                      </div>
                    {{/each-in}}
                  </div>
                </UiCard>
              </div>
            {{/if}}

            <div data-test-impacted-item-details-section>
              <p class="text-sm text-carbon-40 font-medium !mb-0">Impacted item details</p>
              <UiCard class="border-carbon-10">
                {{#if this.eventCategoryComponent}}
                  {{! Render category-specific component }}
                  <this.eventCategoryComponent
                    @eventType={{@event.eventType}}
                    @eventData={{@event.event}}
                    @formatValue={{this.formatValue}}
                  />
                {{else}}
                  <div data-test-default-event-data data-test-event-data>
                    {{! Default rendering for events without category-specific components }}
                    {{#if this.visitorTargetLink}}
                      <DrawerRow
                        @label={{this.visitorTargetLink.label}}
                        @testId="event-visitor-link-row"
                        data-test-event-visitor-link
                      >
                        <LinkTo
                          @route={{this.visitorTargetLink.route}}
                          @model={{this.visitorTargetLink.model}}
                          class="text-carbon-70 hover:text-red-50 underline underline-offset-2"
                          data-test-event-visitor-link-anchor
                        >
                          {{@event.targetName}}
                        </LinkTo>
                      </DrawerRow>
                    {{/if}}

                    {{#if @event.locationName}}
                      <DrawerRow @label="Location" @testId="event-location-row">
                        <span data-test-event-location>{{@event.locationName}}</span>
                      </DrawerRow>
                    {{/if}}

                    {{#if this.eventDataEntries.length}}
                      {{#each this.eventDataEntries as |entry index|}}
                        {{#if entry.isNested}}
                          <div
                            class="{{if (this.includeBorder entry.level index) 'border-t border-carbon-10'}}
                              py-3 first:pt-0 last:pb-0 cursor-pointer hover:bg-carbon-5"
                            style={{this.getIndentationStyle entry.level}}
                            {{on "click" (fn this.toggleFieldCollapse entry.id)}}
                          >
                            <div class="text-sm text-carbon-40 font-medium flex items-center gap-2">
                              {{entry.key}}
                              {{#if (this.isFieldCollapsed entry.id)}}
                                {{svgJar "chevron-down-gray" class="" width="8" height="8"}}
                              {{else}}
                                {{svgJar "chevron-up-gray" class="" width="8" height="8"}}
                              {{/if}}
                            </div>
                          </div>
                        {{else}}
                          <div
                            class="{{if (this.includeBorder entry.level index) 'border-t border-carbon-10'}}
                              py-3 first:pt-0 last:pb-0"
                            style={{this.getIndentationStyle entry.level}}
                          >
                            <div class="text-sm text-carbon-40 mb-1">
                              {{entry.key}}
                              {{#if (this.isMaskedValue entry.value)}}
                                <SensitiveFieldShield />
                              {{else if (this.isRevealableDetail entry.id entry.value)}}
                                <SensitiveRevealButton
                                  @revealed={{this.isSensitiveRevealed "details" entry.id}}
                                  @onToggle={{fn this.toggleSensitiveReveal "details" entry.id}}
                                />
                              {{/if}}
                            </div>
                            <div class="text-sm text-carbon-70">
                              {{#if (this.isMaskedValue entry.value)}}
                                <span class="italic" data-test-masked-detail-value>Hidden</span>
                              {{else if
                                (and
                                  (this.isRevealableDetail entry.id entry.value)
                                  (not (this.isSensitiveRevealed "details" entry.id))
                                )
                              }}
                                <span class="italic" data-test-collapsed-detail-value>Hidden</span>
                              {{else}}
                                {{this.formatValue entry.value}}
                              {{/if}}
                            </div>
                          </div>
                        {{/if}}
                      {{/each}}
                    {{else}}
                      <div class="text-sm text-carbon-40 text-center py-4" data-test-no-event-data>
                        No additional event data available
                      </div>
                    {{/if}}
                  </div>
                {{/if}}
              </UiCard>
            </div>

            <div data-test-event-details-section>
              <p class="text-sm text-carbon-40 font-medium !mb-0">Event details</p>
              <UiCard class="border-carbon-10">
                {{#if @event.dateTime}}
                  <DrawerRow @label="Event timestamp" @testId="event-timestamp-full-row">
                    <div data-test-event-timestamp-full>
                      <span class="whitespace-nowrap">
                        {{this.formatLocalizedDate @event.dateTime @timezone}}
                        {{this.formatLocalizedTimeWithMilliseconds @event.dateTime @timezone}}
                      </span>
                    </div>
                    {{#unless this.hideUtcEquivalent}}
                      <div class="text-carbon-40" data-test-event-timestamp-utc>
                        <span class="whitespace-nowrap">
                          ({{this.formatLocalizedDate @event.dateTime "UTC"}}
                          {{this.formatLocalizedTimeWithMilliseconds @event.dateTime "UTC"}})
                        </span>
                      </div>
                    {{/unless}}
                  </DrawerRow>
                {{/if}}
                {{#if this.isUserActor}}
                  <DrawerRow @label="Actor email" @testId="event-actor-email-row">
                    {{#if this.actorEmailLoader.isLoading}}
                      <span
                        class="inline-block animate-pulse bg-carbon-10 rounded h-5 w-40 align-middle"
                        role="status"
                        aria-label="Loading actor email"
                        data-test-actor-email-loading
                      ></span>
                    {{else}}
                      <span data-test-event-actor-email data-private>
                        {{if this.actorEmailLoader.email this.actorEmailLoader.email "-"}}
                      </span>
                    {{/if}}
                  </DrawerRow>
                {{/if}}
                {{#if (and @event.ipAddress (not this.suppressIpAddress))}}
                  <DrawerRow @label="IP address" @testId="event-ip-address-row">
                    <span data-test-event-ip-address>
                      {{@event.ipAddress}}{{#if @event.country}}
                        <span class="text-carbon-40"> ({{@event.country}})</span>
                      {{/if}}
                    </span>
                  </DrawerRow>
                {{/if}}
                {{#if this.sessionId}}
                  <DrawerRow @label="Session ID" @testId="event-session-id-row">
                    <span data-test-event-session-id>{{this.sessionId}}</span>
                  </DrawerRow>
                {{/if}}
              </UiCard>
            </div>
          </div>
        {{/if}}
      </TableDrawer>
    {{/if}}
  </template>
}
